import { Box, Stack } from "@mui/material";
import parlors from "goi_common/src/constants/parlors_detail.json";

import {
  ParlorRegionHeadSection,
  CTypeCardView,
  DTypeBreadCrumb,
  ETypeRegionHead,
  BTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ParlorDetailProps,
  numberToLocalString,
  GOI_WEB_URL,
  buildParlorRegionPageSchema,
  ParlorRegionFAQ,
  RichSearch,
} from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

// Path 로직 안내
// FUNERAL_SIDO, SIGUNGU의 key로 path 설정.
//
// 1. getStaticPaths 내 values 받아서 "지역 장례식장 추천" 을 추가한 뒤 params로 넘기게 변경
// 2. getStaticProps 내 props sidoName, sigunguName replace 로직 추가

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const sidoList = Array.from(FUNERAL_SIDO).filter(([key, val]) =>
    targetSidos.includes(String(key))
  );

  const sidoPath = sidoList.map(([key, val]) => {
    return {
      params: {
        code: `${val} 지역 장례식장 추천`,
      },
    };
  });

  const sigunguPaths = Array.from(FUNERAL_SIDO)
    .filter(([key, val]) => targetSidos.includes(String(key)))
    .map(([key, val]) => {
      const sigungus = FUNERAL_SIGUNGU.get(key);
      if (sigungus) {
        return Array.from(sigungus.values()).map((sigungu) => {
          return {
            params: {
              code: `${val} 지역-${sigungu} 장례식장 추천`,
            },
          };
        });
      }
    })
    .filter((i) => i != null);

  return {
    paths: sidoPath.concat(sigunguPaths?.flat() as Array<any>),
    fallback: false,
  };
};

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (value === searchValue) return key;
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.code as string;

  if (code.includes("-")) {
    const [sido, sigungu] = code.split("-");
    const sidoName = sido.replace(" 지역", "");
    const sigunguName = sigungu.replace(" 장례식장 추천", "");
    const sidoNumber = getByValue(FUNERAL_SIDO, sidoName);
    const sigunguNumber = getByValue(
      FUNERAL_SIGUNGU.get(sidoNumber as number) as any,
      sigunguName
    );

    const regionParlors = parlors.filter(
      (p) => String(p.sigungu) === String(sigunguNumber)
    );

    const 가족장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 일반장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();

    let parlorsString = "";
    regionParlors.map((p: any) => {
      parlorsString += `${p.companyname} ${numberToLocalString(
        p.binsos[0].charge
      )}원, `;
    });

    const description = `${sidoName} ${sigunguName} 내 ${regionParlors.length}곳의 장례식장을 대상으로 한 비교: 1일 동안 100명의 조문객이 참석할 때의 빈소 요금 및 추가 비용. 서울 및 주변 지역 장례식장 추천 비교 포함 | ${parlorsString}...`;

    return {
      props: {
        regionParlors,
        expectedParlorPrice: { 무빈소장, 가족장, 일반장 },
        sidoName,
        sido: sidoNumber,
        sigunguName,
        sigungu: sigunguNumber,
        isSigungu: true,
        pageDescription: description,
        pageKeywords: `${sidoName} ${sigunguName} 장례식장, ${sidoName} ${sigunguName} 장례식장 추천, ${sidoName} ${sigunguName} 장례식장 비용, ${sidoName} ${sigunguName} 장례식장 평균 예상 비용, ${sidoName} ${sigunguName}  무빈소, ${sidoName} ${sigunguName}  가족장, ${sidoName} ${sigunguName} `,
        pageOgTitle: `${sidoName} ${sigunguName} 지역 장례식장 추천, 비교 | 서울경기인천장례정보포털`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors.length > 0
            ? regionParlors[0]?.images[0]?.url || ""
            : "",
      },
    };
  } else {
    const sidoName = code.replace(" 지역 장례식장 추천", "");
    const sidoNumber = getByValue(FUNERAL_SIDO, sidoName);
    const regionParlors = parlors.filter((p) => p.sido === String(sidoNumber));

    const 가족장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["가족장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 무빈소장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["무빈소"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();
    const 일반장 = Number(
      (
        regionParlors.reduce((prev, curr) => {
          return curr.expected_charge_sum["일반장"] + prev;
        }, 0) / regionParlors.length
      ).toFixed(0)
    ).toLocaleString();

    let parlorsString = "";
    regionParlors.map((p: any) => {
      parlorsString += `${p.companyname} ${numberToLocalString(
        p.binsos[0].charge
      )}원, `;
    });

    const description = `${sidoName} 내 ${regionParlors.length}곳의 장례식장을 대상으로 한 비교: 1일 동안 100명의 조문객이 참석할 때의 빈소 요금 및 추가 비용. 서울 및 주변 지역 장례식장 추천 비교 포함 | ${parlorsString}...`;

    return {
      props: {
        regionParlors,
        expectedParlorPrice: { 무빈소장, 가족장, 일반장 },
        sidoName,
        sido: sidoNumber,
        isSigungu: false,
        pageDescription: description,
        pageKeywords: `${sidoName} 장례식장, ${sidoName} 장례식장 추천, ${sidoName} 장례식장 비용, ${sidoName} 장례식장 평균 예상 비용, ${sidoName} 무빈소, ${sidoName} 가족장, ${sidoName} `,
        pageOgTitle: `${sidoName} 지역 장례식장 추천, 비교 | 서울경기인천장례정보포털`,
        pageOgDescription: description,
        pageOgImage:
          regionParlors.length > 0
            ? regionParlors[0]?.images[0]?.url || ""
            : "",
      },
    };
  }
};

export default function ParlorSlugTypePage({
  regionParlors,
  expectedParlorPrice,
  pageOgTitle,
  sido,
  sidoName,
  sigungu,
  sigunguName,
  isSigungu,
}: {
  regionParlors: Array<ParlorDetailProps>;
  expectedParlorPrice: { 무빈소장: number; 가족장: number; 일반장: number };
  pageOgTitle: string;
  sido: string;
  sidoName: string;
  sigungu?: string;
  sigunguName?: string;
  isSigungu: boolean;
}) {
  if (regionParlors.length === 0) {
    return <></>;
  }

  const parlorRegionPageSchema = buildParlorRegionPageSchema(regionParlors, {
    regionText: `${sidoName}${isSigungu && sigunguName ? ` ${sigunguName}` : ""}`,
    itemUrlBuilder: (id) =>
      `https://www.metropolitan-funeral.co.kr/parlor-places/${id}/`,
  });

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
      </Head>
      <RichSearch data={parlorRegionPageSchema} />
      <ParlorRegionHeadSection
        breadCrumb={
          <DTypeBreadCrumb
            mainColor={regionColor}
            firstItem={{
              text: `장례식장 추천`,
              href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
              event: {
                domain: "metro",
                path: "prlr_recm",
                trigger_and_target: "br_crumb_mth_req",
              },
            }}
            secondItem={{
              text: `${sidoName} 장례식장`,
              href: `/price-funeral/${sidoName} 지역 장례식장 추천/`,
              event: {
                domain: "metro",
                path: "prlr_recm",
                trigger_and_target: "br_crumb_sido_prlr_list",
              },
            }}
            thirdItem={
              isSigungu
                ? {
                    text: `${sigunguName} 장례식장`,
                    href: `/price-funeral/${sidoName} 지역-${sigunguName} 장례식장 추천/`,
                    event: {
                      domain: "metro",
                      path: "prlr_recm",
                      trigger_and_target: "br_crumb_sigungu_prlr_list",
                    },
                  }
                : undefined
            }
            goiHomeEvent={{
              domain: "metro",
              path: "prlr_recm",
              trigger_and_target: "br_crumb_goi_home",
            }}
          />
        }
        title={
          <ETypeRegionHead
            regionParlors={regionParlors}
            sidoName={sidoName}
            sigunguName={sigunguName}
            expectedParlorPrice={expectedParlorPrice}
            mainColor={regionColor}
            event={{
              domain: "metro",
              path: "prlr_recm",
              trigger_and_target: "uv_mth_req",
            }}
          />
        }
        ctaList={[]}
      />

      <BTypeParlorList
        sido={sido}
        sigungu={sigungu}
        title={`${sidoName} ${
          sigunguName ? " " + sigunguName : ""
        }의 다른 장례식장`}
        binsoAveragePrice={true}
        CardViewType={(res) => (
          <CTypeCardView
            sigungu={res.sigungu}
            mainColor={regionColor}
            key={res.companyname}
            name={res.companyname}
            href={`/parlor-places/${res.id}/`}
            sido={res.sido}
            imgSrc={res.images[0] ? res.images[0].url : ""}
            event={{
              domain: "metro",
              path: "prlr_recm",
              trigger_and_target: "uv_sido_prlr_detail",
            }}
          />
        )}
      />
      <Box padding="20px 0 " />
      {isSigungu && (
        <BTypeParlorList
          sido={sido}
          title={`${sidoName}의 다른 장례식장`}
          binsoAveragePrice={true}
          CardViewType={(res) => (
            <CTypeCardView
              sigungu={res.sigungu}
              mainColor={regionColor}
              key={res.companyname}
              name={res.companyname}
              href={`/parlor-places/${res.id}/`}
              sido={res.sido}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "metro",
                path: "prlr_recm",
                trigger_and_target: "uv_sigun_prlr_detail",
              }}
            />
          )}
        />
      )}

      <ParlorRegionFAQ />
    </>
  );
}
